import { el, fmtDate, fmtInt, fmtPct } from '../ui/dom';
import { kpiCard, kpiGrid, panel, segmented } from '../ui/components';
import { barList } from '../ui/charts';
import { getLiveAnalytics, type LiveAnalyticsOverview } from '../api/analytics';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import { summarizeVersionAdoption } from './app-live-analytics-support';

export function liveAnalyticsSection(): HTMLElement {
  let hours = 24;
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastSnapshot: LiveAnalyticsOverview | null = null;
  const controls = el('div', { class: 'growth-controls' });
  const status = el('p', { class: 'panel-sub', role: 'status' }, ['Loading received activity…']);
  const body = el('div', { class: 'stack-gap' });
  const refresh = el('button', { class: 'btn btn-ghost btn-sm', onClick: () => { void load(); } }, ['Refresh']);
  const host = panel('Live received activity', [controls, status, body], {
    subtitle: 'Free, Pro and Teams activity from all app platforms. Reads received events directly; no overnight wait.',
    actions: [refresh],
    wide: true,
  });

  function renderControls(): void {
    const picker = segmented([
      { label: 'Last hour', value: '1' },
      { label: '24 hours', value: '24' },
      { label: '7 days', value: '168' },
    ], String(hours), value => {
      hours = Number(value);
      renderControls();
      void load();
    });
    picker.setAttribute('aria-label', 'Live reporting window');
    picker.querySelectorAll('[role="tab"]').forEach(tab => {
      tab.setAttribute('aria-selected', String(tab.classList.contains('seg-on')));
    });
    controls.replaceChildren(picker);
  }

  function scheduleRefresh(): void {
    if (!host.isConnected) return;
    timer = setTimeout(() => {
      if (!host.isConnected) return;
      if (document.hidden) scheduleRefresh();
      else void load();
    }, 60000);
  }

  async function load(): Promise<void> {
    clearTimeout(timer);
    const requestGeneration = ++generation;
    refresh.disabled = true;
    status.className = 'panel-sub';
    status.textContent = `Loading the ${hours}-hour window… ` +
      (lastSnapshot ? `Counts below are still the ${lastSnapshot.window.hours}-hour snapshot from ${fmtDate(lastSnapshot.generatedAt)}.` : 'Counts are not available yet.');
    try {
      const snapshot = await getLiveAnalytics(hours);
      if (requestGeneration !== generation || !host.isConnected) return;
      lastSnapshot = snapshot;
      status.textContent = `Updated ${fmtDate(snapshot.generatedAt)} · ` +
        (snapshot.totals.latestEventAt ? `latest event received ${fmtDate(snapshot.totals.latestEventAt)}` : 'no events received in this window') +
        ' · refreshes every minute while this page is visible.';
      status.className = 'panel-sub';
      body.replaceChildren(renderSnapshot(snapshot));
    } catch (err) {
      if (requestGeneration !== generation || !host.isConnected) return;
      if (err instanceof UnauthorizedError) return navigate('/login');
      status.className = 'banner banner-error';
      status.textContent = 'Could not refresh live activity. ' +
        (lastSnapshot ? `Showing the earlier ${lastSnapshot.window.hours}-hour snapshot from ${fmtDate(lastSnapshot.generatedAt)}.` : 'Counts are unavailable; this does not mean zero activity.');
    } finally {
      if (requestGeneration === generation) {
        refresh.disabled = false;
        scheduleRefresh();
      }
    }
  }

  renderControls();
  queueMicrotask(() => { if (host.isConnected) void load(); });
  return host;
}

function renderSnapshot(snapshot: LiveAnalyticsOverview): HTMLElement {
  const totals = snapshot.totals;
  const children: HTMLElement[] = [
    kpiGrid([
      kpiCard('Reporting devices', fmtInt(totals.activeDevices), { sub: 'latest version reported in this window' }),
      kpiCard('Reported scans', fmtInt(totals.scans), { sub: `${fmtInt(totals.scanningDevices)} devices reported scans` }),
      kpiCard('Sessions ended', fmtInt(totals.sessions)),
      kpiCard('Events received', fmtInt(totals.events)),
    ]),
    el('p', { class: 'panel-sub' }, [
      'Devices use rotating analytics identifiers: these are active reporting devices, not unique people or total installs. ' +
      'Each device is assigned to its latest received version in this window; upgrading does not count it twice. ' +
      'This live view covers at most the last seven days.',
    ]),
  ];
  const adoption = summarizeVersionAdoption(snapshot.versions);
  if (adoption.length) {
    children.push(el('h3', { class: 'panel-title' }, ['Latest reported app versions']));
    children.push(barList(adoption.map(row => ({
      label: row.appVersion, value: row.activeDevices,
    }))));
  }
  if (snapshot.versions.length) {
    children.push(el('div', { class: 'table-wrap' }, [
      el('table', { class: 'data-table' }, [
        el('thead', {}, [el('tr', {}, ['Version / build', 'Platform', 'Reporting devices', 'Device share', 'Reported scans', 'Events'].map(label => el('th', {}, [label])))]),
        el('tbody', {}, snapshot.versions.map(row => el('tr', {}, [
          el('td', {}, [`${row.appVersion} / ${row.buildNumber}`]),
          el('td', {}, [row.platform]),
          el('td', {}, [fmtInt(row.activeDevices)]),
          el('td', {}, [fmtPct(row.deviceShare)]),
          el('td', {}, [fmtInt(row.scans)]),
          el('td', {}, [fmtInt(row.events)]),
        ]))),
      ]),
    ]));
    children.push(el('p', { class: 'panel-sub' }, [
      'Scans and events stay attributed to the version that sent them; device counts use the latest version. ' +
      `${fmtInt(totals.onboardingCompletions)} onboarding completion events · ${fmtInt(totals.unidentifiedEvents)} events without a device identifier. ` +
      'Version/build reporting does not distinguish App Store from TestFlight.',
    ]));
  }
  if (snapshot.scanMethods.length) {
    children.push(el('h3', { class: 'panel-title' }, ['Reported scan methods']));
    children.push(barList(snapshot.scanMethods.map(row => ({ label: row.method, value: row.scans }))));
  }
  return el('div', { class: 'stack-gap' }, children);
}
